import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
    container: {
        padding: 10,
    },
    sectionTitle: {
        fontSize: 16,
        fontWeight: 'bold',
        marginBottom: 15,
        color: '#1e40af',
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
        paddingBottom: 5,
    },
    table: {
        display: 'flex',
        width: '100%',
        borderWidth: 1,
        borderColor: '#e5e7eb',
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#e5e7eb',
        minHeight: 30,
        alignItems: 'center',
        minPresenceAhead: 50,
        orphans: 3,
        widows: 3,
        pageBreakInside: 'avoid',
        breakInside: 'avoid',
    },
    tableHeader: {
        backgroundColor: '#f0f9ff',
        fontWeight: 'bold',
    },
    tableCell: {
        padding: 5,
        fontSize: 8,
        borderRightWidth: 1,
        borderRightColor: '#e5e7eb',
        textAlign: 'center',
        justifyContent: 'center',
        alignItems: 'center',
    },
    optionNoCell: {
        width: '12%',
        fontWeight: 'bold',
        color: '#1e40af',
    },
    productCell: {
        width: '15%',
    },
    primaryCell: {
        width: '20%',
        fontSize: 7,
    },
    similarityCell: {
        width: '10%',
        fontWeight: 'bold',
    },
    secondaryCell: {
        width: '20%',
        fontSize: 7,
    },
    palletCell: {
        width: '13%',
        fontSize: 7,
    },
    transitCell: {
        width: '10%',
        fontSize: 7,
    },
    narrowCell: {
        width: '9%',
        fontSize: 7,
    },
    wideCell: {
        width: '16%',
        fontSize: 7,
    },
    subsectionTitle: {
        fontSize: 13,
        fontWeight: 'bold',
        marginTop: 10,
        marginBottom: 8,
        color: '#1e40af',
    },
    selectedIndicator: {
        backgroundColor: '#dcfce7',
        color: '#166534',
        fontWeight: 'bold',
    },
    noDataText: {
        fontSize: 12,
        color: '#6b7280',
        textAlign: 'center',
        marginTop: 20,
    },
});

interface ChooseTablePDFProps {
    data: Array<{
        optionNo: string;
        sourceType?: string;
        palletVariant?: string;
        product: string;
        primary: {
            details: string;
            similarity: number;
        };
        secondary: {
            details: string;
            similarity: number;
        };
        tertiary?: {
            details: string;
            similarity: number | null;
        };
        palletSimilarity?: number | null;
        casesPerLayer?: string;
        layersPerPallet?: string;
        pallet_pattern: string;
        transit: string;
        selected: boolean;
    }>;
}

type ChooseReportRow = ChooseTablePDFProps['data'][number];

type ChooseReportColumnKey =
    | 'optionNo'
    | 'palletVariant'
    | 'product'
    | 'primaryDetails'
    | 'primarySimilarity'
    | 'secondaryDetails'
    | 'secondarySimilarity'
    | 'tertiaryDetails'
    | 'tertiarySimilarity'
    | 'unitload'
    | 'unitloadSimilarity'
    | 'transit';

const CHOOSE_REPORT_COLUMN_WEIGHTS: Record<ChooseReportColumnKey, number> = {
    optionNo: 0.9,
    palletVariant: 0.9,
    product: 1.25,
    primaryDetails: 1.65,
    primarySimilarity: 0.9,
    secondaryDetails: 1.65,
    secondarySimilarity: 0.9,
    tertiaryDetails: 1.65,
    tertiarySimilarity: 0.9,
    unitload: 1.1,
    unitloadSimilarity: 0.9,
    transit: 1.2,
};

export const getChooseReportColumnWidths = (columns: ChooseReportColumnKey[]) => {
    const totalWeight = columns.reduce((sum, column) => sum + CHOOSE_REPORT_COLUMN_WEIGHTS[column], 0);
    let allocatedWidth = 0;

    return columns.reduce<Partial<Record<ChooseReportColumnKey, number>>>((widths, column, index) => {
        const width = index === columns.length - 1
            ? 100 - allocatedWidth
            : Number(((CHOOSE_REPORT_COLUMN_WEIGHTS[column] / totalWeight) * 100).toFixed(6));
        widths[column] = width;
        allocatedWidth += width;
        return widths;
    }, {});
};

export const splitChooseRowsForReport = (data: ChooseReportRow[]) => ({
    specifications: data.filter(row => row.sourceType !== 'physical'),
    physicalShipTests: data.filter(row => row.sourceType === 'physical'),
});

const getChooseRowSimilarityScore = (row: ChooseReportRow) => {
    const similarities = [
        row.primary?.similarity,
        row.secondary?.similarity,
        row.tertiary?.similarity,
        row.palletSimilarity,
    ].filter((value): value is number => typeof value === 'number' && Number.isFinite(value));

    return similarities.length
        ? similarities.reduce((sum, value) => sum + value, 0) / similarities.length
        : 0;
};

export const orderChooseRowsForReport = (data: ChooseReportRow[]) => {
    const { specifications, physicalShipTests } = splitChooseRowsForReport(data);
    const sortedSpecifications = [...specifications].sort((a, b) => b.secondary.similarity - a.secondary.similarity);
    const physicalRowsBySecondarySimilarity = [...physicalShipTests]
        .sort((a, b) => b.secondary.similarity - a.secondary.similarity);
    const groupedPhysicalRows = new Map<string, ChooseReportRow[]>();

    physicalRowsBySecondarySimilarity.forEach((row) => {
        const optionNo = row.optionNo || 'N/A';
        const group = groupedPhysicalRows.get(optionNo) || [];
        group.push(row);
        groupedPhysicalRows.set(optionNo, group);
    });

    return {
        specifications: sortedSpecifications,
        physicalShipTests: Array.from(groupedPhysicalRows.values()).flatMap((rows) => (
            [...rows].sort((a, b) => getChooseRowSimilarityScore(b) - getChooseRowSimilarityScore(a))
        )),
    };
};

export const formatUnitloadDetailsForReport = (row: ChooseReportRow) => {
    const details = [
        row.pallet_pattern,
        row.casesPerLayer ? `Cases/Layer: ${row.casesPerLayer}` : '',
        row.layersPerPallet ? `Layers/Pallet: ${row.layersPerPallet}` : '',
    ].filter(Boolean);

    return details.length > 0 ? details.join('\n') : 'N/A';
};

const formatSimilarity = (similarity?: number | null) => (
    typeof similarity === 'number' && Number.isFinite(similarity) ? `${similarity}%` : '-'
);

const renderTable = (title: string, rows: ChooseReportRow[], includePalletVariant: boolean, includeTransit: boolean) => {
    if (rows.length === 0) {
        return (
            <View style={{ marginBottom: 15 }}>
                <Text style={styles.subsectionTitle}>{title}</Text>
                <Text style={styles.noDataText}>No matching configuration</Text>
            </View>
        );
    }

    const showUnitloadColumn = rows.some(row => formatUnitloadDetailsForReport(row) !== 'N/A');
    const showTertiaryColumns = rows.some(row => (
        Boolean(row.tertiary?.details) &&
        typeof row.tertiary?.similarity === 'number' &&
        Number.isFinite(row.tertiary.similarity)
    ));
    const showUnitloadSimilarityColumn = showUnitloadColumn && rows.some(row => (
        typeof row.palletSimilarity === 'number' && Number.isFinite(row.palletSimilarity) && row.palletSimilarity > 0
    ));
    const activeColumns: ChooseReportColumnKey[] = [
        'optionNo',
        ...(includePalletVariant ? ['palletVariant' as const] : []),
        'product',
        'primaryDetails',
        'primarySimilarity',
        'secondaryDetails',
        'secondarySimilarity',
        ...(showTertiaryColumns ? ['tertiaryDetails' as const, 'tertiarySimilarity' as const] : []),
        ...(showUnitloadColumn ? ['unitload' as const] : []),
        ...(showUnitloadSimilarityColumn ? ['unitloadSimilarity' as const] : []),
        ...(includeTransit ? ['transit' as const] : []),
    ];
    const columnWidths = getChooseReportColumnWidths(activeColumns);
    const columnWidthStyle = (column: ChooseReportColumnKey) => ({ width: `${columnWidths[column]}%` });

    return (
        <View style={{ marginBottom: 15 }}>
            <Text style={styles.subsectionTitle}>{title}</Text>
            <View style={styles.table}>
                <View style={[styles.tableRow, styles.tableHeader]}>
                    <Text style={[styles.tableCell, styles.narrowCell, columnWidthStyle('optionNo')]}>Option No</Text>
                    {includePalletVariant && <Text style={[styles.tableCell, styles.narrowCell, columnWidthStyle('palletVariant')]}>Pallet Variant</Text>}
                    <Text style={[styles.tableCell, styles.narrowCell, columnWidthStyle('product')]}>Product</Text>
                    <Text style={[styles.tableCell, styles.wideCell, columnWidthStyle('primaryDetails')]}>Primary Details</Text>
                    <Text style={[styles.tableCell, styles.narrowCell, columnWidthStyle('primarySimilarity')]}>Primary Similarity %</Text>
                    <Text style={[styles.tableCell, styles.wideCell, columnWidthStyle('secondaryDetails')]}>Secondary Details</Text>
                    <Text style={[styles.tableCell, styles.narrowCell, columnWidthStyle('secondarySimilarity')]}>Secondary Similarity %</Text>
                    {showTertiaryColumns && <Text style={[styles.tableCell, styles.wideCell, columnWidthStyle('tertiaryDetails')]}>Tertiary</Text>}
                    {showTertiaryColumns && <Text style={[styles.tableCell, styles.narrowCell, columnWidthStyle('tertiarySimilarity')]}>Tertiary Similarity %</Text>}
                    {showUnitloadColumn && <Text style={[styles.tableCell, styles.narrowCell, columnWidthStyle('unitload')]}>Unitload</Text>}
                    {showUnitloadSimilarityColumn && <Text style={[styles.tableCell, styles.narrowCell, columnWidthStyle('unitloadSimilarity')]}>Unitload Similarity %</Text>}
                    {includeTransit && <Text style={[styles.tableCell, styles.narrowCell, columnWidthStyle('transit')]}>Transit</Text>}
                </View>

                {rows.map((row, index) => {
                    const rowKey = `${row.optionNo}-${row.palletVariant || ''}-${index}`;

                    return (
                        <View
                            key={rowKey}
                            style={[
                                styles.tableRow,
                                row.selected && styles.selectedIndicator,
                                {
                                    minPresenceAhead: 50,
                                    breakBefore: false,
                                    breakAfter: false,
                                    breakInside: false,
                                    pageBreakInside: 'avoid',
                                }
                            ]}
                            break={false}
                            wrap={false}
                        >
                            <Text style={[styles.tableCell, styles.narrowCell, columnWidthStyle('optionNo')]}>{row.optionNo}</Text>
                            {includePalletVariant && (
                                <Text style={[styles.tableCell, styles.narrowCell, columnWidthStyle('palletVariant')]}>
                                    {row.palletVariant || 'N/A'}
                                </Text>
                            )}
                            <Text style={[styles.tableCell, styles.narrowCell, columnWidthStyle('product')]}>{row.product}</Text>
                            <Text style={[styles.tableCell, styles.wideCell, columnWidthStyle('primaryDetails')]}>{row.primary.details}</Text>
                            <Text style={[styles.tableCell, styles.narrowCell, columnWidthStyle('primarySimilarity')]}>{row.primary.similarity}%</Text>
                            <Text style={[styles.tableCell, styles.wideCell, columnWidthStyle('secondaryDetails')]}>{row.secondary.details}</Text>
                            <Text style={[styles.tableCell, styles.narrowCell, columnWidthStyle('secondarySimilarity')]}>{row.secondary.similarity}%</Text>
                            {showTertiaryColumns && (
                                <Text style={[styles.tableCell, styles.wideCell, columnWidthStyle('tertiaryDetails')]}>{row.tertiary?.details || '-'}</Text>
                            )}
                            {showTertiaryColumns && (
                                <Text style={[styles.tableCell, styles.narrowCell, columnWidthStyle('tertiarySimilarity')]}>{formatSimilarity(row.tertiary?.similarity)}</Text>
                            )}
                            {showUnitloadColumn && (
                                <Text style={[styles.tableCell, styles.narrowCell, columnWidthStyle('unitload')]}>{formatUnitloadDetailsForReport(row)}</Text>
                            )}
                            {showUnitloadSimilarityColumn && (
                                <Text style={[styles.tableCell, styles.narrowCell, columnWidthStyle('unitloadSimilarity')]}>{formatSimilarity(row.palletSimilarity)}</Text>
                            )}
                            {includeTransit && <Text style={[styles.tableCell, styles.narrowCell, columnWidthStyle('transit')]}>{row.transit}</Text>}
                        </View>
                    );
                })}
            </View>
        </View>
    );
};

export const ChooseTablePDF: React.FC<ChooseTablePDFProps> = ({ data }) => {
    if (!data || data.length === 0) {
        return (
            <View style={styles.container}>
                <Text style={styles.sectionTitle}>Similar Cases Analysis</Text>
                <Text style={styles.noDataText}>No matching configuration</Text>
            </View>
        );
    }

    const { specifications, physicalShipTests } = orderChooseRowsForReport(data);

    return (
        <View style={styles.container}>
            <Text style={styles.sectionTitle}>Similar Cases Analysis</Text>
            {renderTable('Specifications', specifications, false, false)}
            {renderTable('Physical Ship Tests', physicalShipTests, true, true)}

            {/* Legend */}
            <View style={{ marginTop: 10, paddingTop: 6, paddingBottom: 4, paddingHorizontal: 8, backgroundColor: '#f9fafb' }}>
                <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 3 }}>Legend:</Text>
                <Text style={{ fontSize: 9, marginBottom: 1 }}>
                    • Cases highlighted in green were selected for further analysis
                </Text>
                <Text style={{ fontSize: 9, marginBottom: 1 }}>
                    • Similarity percentages indicate how closely each case matches the current project
                </Text>
                <Text style={{ fontSize: 9 }}>
                    • Specifications are sorted by secondary similarity; physical ship tests are grouped by option
                </Text>
            </View>
        </View>
    );
}; 
